import { useTranslation } from 'react-i18next';
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    VStack,
    Input,
    Select,
    IconButton,
    Box,
    HStack,
} from '@chakra-ui/react';
import { RotateCcw } from 'lucide-react';
import { useAppStore } from '../../state/store';
import { ManageDialogFrame, ManageDialogCloseFooter } from './ManageDialogFrame';
import { useMaterialNameOptions } from '../typeProperties/materialCatalog';
import {
    useManageSettingsStore,
    DEFAULT_OBJECT_STYLE_CATEGORIES,
    DEFAULT_OBJECT_STYLE,
    ObjectStyle,
} from '../../state/useManageSettingsStore';

const LINE_WEIGHT_OPTIONS = [
    0.13, 0.18, 0.25, 0.35, 0.5, 0.7, 1, 1.4, 2,
];

const LINE_PATTERN_OPTIONS: ObjectStyle['linePattern'][] = [
    'Solid',
    'Dash',
    'Dot',
    'Dash Dot',
    'Long Dash',
];

export function ObjectStylesDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'objectStyles');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const objectStyles = useManageSettingsStore((state) => state.objectStyles);
    const updateObjectStyle = useManageSettingsStore((state) => state.updateObjectStyle);
    const resetObjectStyle = useManageSettingsStore((state) =>state.resetObjectStyle); const categories = DEFAULT_OBJECT_STYLE_CATEGORIES; const materialNames = useMaterialNameOptions(); return (<ManageDialogFrame
            title={t('manage.objectStyles')}
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            footer={<ManageDialogCloseFooter onClose={onClose} />}
        >
            <VStack spacing={3} align="stretch">
                <Text fontSize="xs" color="var(--color-text-secondary)">{t('manage.lineWeightMmLineColorLinePatternAndDefaultMaterialPerObjectCateg')}</Text>
                <Box overflowX="auto" maxH="55vh">
                    <Table size="sm" variant="unstyled">
                        <Thead position="sticky" top={0} bg="var(--color-bg-secondary)" zIndex={1}>
                            <Tr>
                                <Th color="var(--color-text-secondary)" fontSize="xs">{t('manage.category')}</Th>
                                <Th color="var(--color-text-secondary)" fontSize="xs">{t('manage.lineWeight')}</Th>
                                <Th color="var(--color-text-secondary)" fontSize="xs">{t('manage.lineColor')}</Th>
                                <Th color="var(--color-text-secondary)" fontSize="xs">{t('manage.linePattern')}</Th>
                                <Th color="var(--color-text-secondary)" fontSize="xs">{t('manage.material')}</Th>
                                <Th color="var(--color-text-secondary)" fontSize="xs" width="40px" />
                            </Tr>
                        </Thead>
                        <Tbody>
                            {categories.map((category) => {
                                const style = objectStyles[category] ?? DEFAULT_OBJECT_STYLE;
                                const isModified =
                                    style.lineWeight !== DEFAULT_OBJECT_STYLE.lineWeight ||
                                    style.lineColor !== DEFAULT_OBJECT_STYLE.lineColor ||
                                    style.linePattern !== DEFAULT_OBJECT_STYLE.linePattern ||
                                    (style.materialName || '') !== DEFAULT_OBJECT_STYLE.materialName;
                                return (
                                    <Tr key={category}>
                                        <Td fontSize="sm" borderColor="var(--color-border)" whiteSpace="nowrap">
                                            {category}
                                        </Td>
                                        <Td borderColor="var(--color-border)">
                                            <Select
                                                size="xs"
                                                width="90px"
                                                value={style.lineWeight}
                                                onChange={(e) =>
                                                    updateObjectStyle(category, {
                                                        lineWeight: parseFloat(e.target.value),
                                                    })
                                                }
                                                bg="var(--color-bg-tertiary)"
                                                borderColor="var(--color-border)"
                                            >
                                                {LINE_WEIGHT_OPTIONS.map((w) => (
                                                    <option key={w} value={w}>{w}</option>
                                                ))}
                                            </Select>
                                        </Td>
                                        <Td borderColor="var(--color-border)">
                                            <HStack spacing={2}>
                                                <Box
                                                    width="20px"
                                                    height="12px"
                                                    border="1px solid var(--color-border)"
                                                    bg={style.lineColor}
                                                />
                                                <Input
                                                    size="xs"
                                                    width="100px"
                                                    type="color"
                                                    value={style.lineColor}
                                                    onChange={(e) =>
                                                        updateObjectStyle(category, { lineColor: e.target.value })
                                                    }
                                                    bg="var(--color-bg-tertiary)"
                                                    borderColor="var(--color-border)"
                                                    padding={0}
                                                />
                                            </HStack>
                                        </Td>
                                        <Td borderColor="var(--color-border)">
                                            <Select
                                                size="xs"
                                                width="110px"
                                                value={style.linePattern}
                                                onChange={(e) =>
                                                    updateObjectStyle(category, {
                                                        linePattern: e.target.value as ObjectStyle['linePattern'],
                                                    })
                                                }
                                                bg="var(--color-bg-tertiary)"
                                                borderColor="var(--color-border)"
                                            >
                                                {LINE_PATTERN_OPTIONS.map((p) => (
                                                    <option key={p} value={p}>{p}</option>
                                                ))}
                                            </Select>
                                        </Td>
                                        <Td borderColor="var(--color-border)">
                                            <Select
                                                size="xs"
                                                width="150px"
                                                value={style.materialName || ''}
                                                onChange={(e) =>
                                                    updateObjectStyle(category, { materialName: e.target.value })
                                                }
                                                bg="var(--color-bg-tertiary)"
                                                borderColor="var(--color-border)"
                                            >
                                                <option value="">—</option>
                                                {materialNames.map((m) => (
                                                    <option key={m} value={m}>{m}</option>
                                                ))}
                                            </Select>
                                        </Td>
                                        <Td borderColor="var(--color-border)">
                                            {isModified ? (
                                                <IconButton
                                                    aria-label={`Reset ${category}`}
                                                    title={t('manage.resetToDefault')}
                                                    icon={<RotateCcw size={12} />}
                                                    size="xs"
                                                    variant="ghost"
                                                    onClick={() => resetObjectStyle(category)}
                                                />
                                            ) : null}
                                        </Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </Box>
            </VStack>
        </ManageDialogFrame>
    );
}
